import "./App.css";
import React from "react";
import { IonApp, IonRouterOutlet } from "@ionic/react";
import { Redirect, Route, Switch } from "react-router-dom";
import { IonReactRouter } from "@ionic/react-router";
import { datadogRum } from "@datadog/browser-rum";
import { setupIonicReact } from "@ionic/react";
import "react-datepicker/dist/react-datepicker.css";

import "./App.css";
import cookie, { cookieNames } from "./libs/cookie";
import { useReducerWithEnhancedDispatch } from "./libs/enhancedDispatch";
import { diff } from "./libs/Util";
import { TEACHER_SESSION_KEY } from "./Definition";
import {
  reducer,
  Context,
  fetchInitialData,
  InitialAppState,
  AppState,
  Actions,
  updateTeacherPasswordPath,
  homePath,
  manageBoxesPath,
  manageBoxDirectoriesPath,
  boxesPath,
  prefixPath,
  editProfilePath,
  notificationListPath,
  submittedRealizationListPath,
  sharedRealizationListPath,
  reportListPath,
  sharedRealizationPath,
  resetTeacherPasswordPath,
  sendEmailCompletePath,
  authenticationPath,
  LOGOUT_SUCCESS_MESSAGE,
  gearthemesListPath,
  realizationsTeacherPath,
  realizationsBoxPath,
} from "./store/teacher";
import SideMenu from "./molecules/teacher/SideMenu";
import GlobalHeader from "./organisms/teacher/Header/GlobalHeader";
import Error503Page from "./templates/Error503Page";
import Login from "./pages/teacher/Login";
import MultiFactorAuthentication from "./pages/teacher/MultiFactorAuthentication";
import UpdateTeacherPassword from "./pages/teacher/UpdateTeacherPassword";
import EditProfile from "./pages/teacher/EditProfile";
import SharedRealizationListPage from "./pages/teacher/SharedRealizationListPage";
import Reports from "./pages/teacher/Reports";
import Notifications from "./pages/teacher/Notifications";
import { SharedRealizationPage } from "./pages/teacher/SharedRealizationPage";
import ManageBoxesPage from "./pages/teacher/ManageBoxesPage";
import ManageBoxDirectoriesPage from "./pages/teacher/ManageBoxDirectoriesPage";
import BoxDashboardPage from "./pages/teacher/BoxDashboardPage";
import RealizationsTeacherPage from "./pages/teacher/RealizationsTeacherPage";
import RealizationsBoxPage from "./pages/teacher/RealizationsBoxPage";
import BoxDetailPage from "./pages/teacher/BoxDetailPage";
import ResetPassword from "./pages/teacher/ResetPassword";
import SendEmailCompletePage from "./pages/teacher/SendEmailCompletePage";
import TimelineThemePage from "./pages/teacher/TimelineTheme";

setupIonicReact({
  mode: "ios",
});

const reducerForApp =
  process.env.NODE_ENV === "production"
    ? reducer
    : (contextState: AppState, action: Actions) => {
        console.groupCollapsed(action.types);
        console.log("action", action);
        const result = reducer(contextState, action);
        console.log("diff", diff(contextState, result));
        console.log("prevState", contextState);
        console.log("currentState", result);
        console.groupEnd();
        return result;
      };

function TeacherApp() {
  if (window.location.href.match(/session_id=/)) {
    const result = window.location.href.split("session_id=").pop() as string;
    const sessionID = result.replace(/&.*$/, "");
    sessionStorage.setItem(TEACHER_SESSION_KEY, sessionID);
  }
  const savedSessionId =
    sessionStorage.getItem(TEACHER_SESSION_KEY) ||
    cookie.getCookie(cookieNames.teacher_session_id) ||
    "";
  const [contextState, dispatch] = useReducerWithEnhancedDispatch({
    reducer: reducerForApp,
    initialAppState: {
      ...InitialAppState,
      session_id: savedSessionId,
    },
  });

  React.useEffect(() => {
    if (contextState.teacher.id) {
      datadogRum.setUser({
        id: contextState.teacher.id,
        type: "teacher",
        school_id: contextState.teacher.school_id,
      });
    }
  }, [contextState.teacher.id, contextState.teacher.school_id]);

  React.useEffect(() => {
    if (contextState.session_id) {
      if (window.location.href.match(/session_id=/)) {
        window.history.pushState(null, "", window.location.pathname);
      }
      dispatch(fetchInitialData());
    }
  }, [contextState.session_id, dispatch]);

  React.useEffect(() => {
    if (
      savedSessionId &&
      !sessionStorage.getItem(TEACHER_SESSION_KEY) &&
      !cookie.getCookie(cookieNames.teacher_session_id) &&
      !contextState.session_id
    ) {
      window.location.reload();
    } else if (!savedSessionId && contextState.session_id) {
      cookie.set(cookieNames.teacher_session_id, contextState.session_id, 365);
    } else if (savedSessionId && !contextState.session_id) {
      sessionStorage.removeItem(TEACHER_SESSION_KEY);
      cookie.deleteAll();
    }
  }, [savedSessionId, contextState.session_id]);

  React.useEffect(() => {
    if (
      contextState.success_message === LOGOUT_SUCCESS_MESSAGE ||
      contextState.expires < new Date()
    ) {
      window.location.reload();
    }
  }, [contextState]);

  return contextState.isServiceUnavailable ? (
    <Error503Page />
  ) : (
    <IonApp>
      <Context.Provider value={{ contextState, dispatch }}>
        {contextState.session_id ? (
          <>
            <IonReactRouter basename={prefixPath}>
              <GlobalHeader />
              <SideMenu />
              <IonRouterOutlet mode="ios" id="sideMenuContainer">
                <Route
                  exact
                  path={submittedRealizationListPath}
                  component={BoxDashboardPage}
                />
                <Route
                  exact
                  path={realizationsTeacherPath}
                  component={RealizationsTeacherPage}
                />
                <Route
                  exact
                  path={`${realizationsBoxPath}/:id`}
                  component={RealizationsBoxPage}
                />
                <Route
                  path={`${sharedRealizationPath}:id`}
                  component={SharedRealizationPage}
                />
                <Route
                  exact
                  path={sharedRealizationListPath}
                  component={SharedRealizationListPage}
                />
                <Route exact path={reportListPath} component={Reports} />
                <Route exact path={editProfilePath} component={EditProfile} />
                <Route
                  exact
                  path={manageBoxesPath}
                  component={ManageBoxesPage}
                />
                <Route
                  exact
                  path={manageBoxDirectoriesPath}
                  component={ManageBoxDirectoriesPage}
                />
                <Route
                  exact
                  path={`${boxesPath}/:id`}
                  component={BoxDetailPage}
                />
                <Route
                  exact
                  path={`${sharedRealizationListPath}${boxesPath}/:id`}
                  component={BoxDetailPage}
                />
                <Route
                  exact
                  path={notificationListPath}
                  component={Notifications}
                />
                <Route
                  exact
                  path={`${gearthemesListPath}/:id`}
                  component={TimelineThemePage}
                />
                <Redirect
                  exact
                  path={homePath}
                  to={submittedRealizationListPath}
                />
                <Redirect
                  exact
                  path={`${authenticationPath}/:ott`}
                  to={submittedRealizationListPath}
                />
              </IonRouterOutlet>
            </IonReactRouter>
          </>
        ) : (
          <IonReactRouter basename={prefixPath}>
            <Switch>
              <Route
                path={`${updateTeacherPasswordPath}/:uuid`}
                component={UpdateTeacherPassword}
              />
              <Route
                path={`${resetTeacherPasswordPath}`}
                component={ResetPassword}
              />
              <Route
                path={`${sendEmailCompletePath}`}
                component={SendEmailCompletePage}
              />
              <Route
                path={`${authenticationPath}/:ott`}
                component={MultiFactorAuthentication}
              />
              <Route exact path={homePath} component={Login} />
              <Redirect to={homePath} />
            </Switch>
          </IonReactRouter>
        )}
      </Context.Provider>
    </IonApp>
  );
}

export default TeacherApp;
